// Current engineering team roster for per-engineer charts. Historical rows
// contain ex-team members and (pre-alias) duplicate identities; display
// filters to this list so per-engineer cards compare exactly the people on
// the team today. Ingestion is untouched — the raw rows keep everything.
// Like chart-specs, this module is intentionally dependency-free so root
// vitest tests can import it directly.

/**
 * Older ingested rows carry superseded canonical emails (a legacy Jira
 * account transliterated the same person differently). Aliases rewrite them
 * onto the current identity; the pivot layer accumulates rows that land on
 * the same (period, engineer) key, so merged histories add up correctly.
 */
export const ENGINEER_ALIASES: Record<string, string> = {
	"tatiana.serikova@aihr.com": "tetiana.sierikova@aihr.com"
};

export const TEAM_ROSTER: readonly string[] = [
	// Member squad
	"aliia.ioanu@aihr.com",
	"andressa.machado@aihr.com",
	"igor.klimenko@aihr.com",
	"dania.noseir@aihr.com",
	// B2B squad
	"hossein.mousavi@aihr.com",
	"karahan.ozen@aihr.com",
	"moien.tajik@aihr.com",
	"tetiana.sierikova@aihr.com",
	// Head of Engineering
	"gustavo.coelho@aihr.com",
	"bender@aihr.com"
];

const ROSTER_SET = new Set(TEAM_ROSTER);

export const canonicalEngineer = (raw: string): string => {
	const lower = raw.trim().toLowerCase();
	return ENGINEER_ALIASES[lower] ?? lower;
};

export const isRosterEngineer = (raw: string | null): boolean =>
	raw !== null && ROSTER_SET.has(canonicalEngineer(raw));

/**
 * Keep only rows attributed to current team members, rewriting aliased
 * identities onto their canonical email. Rows without an engineer are
 * dropped — this filter only runs for engineer-series charts, where such
 * rows would render as an "unknown" series.
 */
export const filterRowsToRoster = <T extends { engineer: string | null }>(
	rows: T[]
): T[] =>
	rows.flatMap((row) => {
		if (!isRosterEngineer(row.engineer)) {
			return [];
		}
		const canonical = canonicalEngineer(row.engineer as string);
		return [row.engineer === canonical ? row : { ...row, engineer: canonical }];
	});
